import { useState } from 'react';

import { api } from '../api.js';
import ApplicationPicker from './ApplicationPicker.jsx';
import { toast } from './Toasts.jsx';

// T392 — the unplaced-mail card, lifted out of MailPage so Tasks can show it.
//
// He asked why an obvious confirmation was not associated and no task opened.
// Everything needed to place it already existed — suggestion chips, a typed
// picker over 87 applications, and "I applied here but it is not in the
// tracker" — but all of it lived on the Mail page, so mail waiting on a
// decision never appeared as work. Moving the component rather than writing
// a second one, because two implementations of "place this message" would
// drift the first time either changed.

function fmt(d) {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const WHY = {
  platform: 'same company on the recruiting platform',
  domain: 'same company domain',
  named: 'company named in the message',
};

function Row({ email, candidatures, onLinked, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // T411 — the card deletes; the host is only told the row is gone.
  //
  // This used to be the host's job, and the two hosts disagreed: MailPage
  // called DELETE and Tasks passed its local drop straight in as `onDeleted`,
  // so on Tasks the toast said "Copy deleted", the row vanished, and it came
  // back on the next load because nothing had been deleted.
  //
  // That is exactly the drift the T392 comment above predicted when it chose
  // to MOVE this component rather than write a second one — and it happened
  // anyway, because the behaviour stayed behind in the hosts. Placing the
  // mail already lives here (`link`, `create`); deleting it belongs here too,
  // and then neither host can get it wrong.
  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/emails/${email._id}`);
      onDeleted(email._id);
      toast('Copy deleted. The mail is still in your mailbox.', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || 'Could not delete that copy.', { kind: 'error' });
    } finally {
      setDeleting(false);
    }
  };
  const [body, setBody] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  // T369 — for the messages whose company he never recorded an application
  // for. Prefilled from the ATS tenant; the role only he knows.
  const [adding, setAdding] = useState(false);
  // T392 — both fields arrive filled in from the message.
  //
  // The company hint existed; the ROLE did not, so he retyped
  // "Infrastrukturarkitekt" out of a subject that had just said it. The
  // server reads both now (`applicationHint`), and `companyHint` stays as
  // the fallback so nothing regresses where the new reader finds nothing.
  const hint = email.applicationHint ?? {};
  // A role read out of the message is the signal that this is a real
  // application we simply do not have. A company alone is not — every Amazon
  // assessment reminder has one, and those belong to an application he
  // already has.
  const noMatchButReadable = (email.suggestions ?? []).length === 0 && Boolean(hint.title);
  const [newTitle, setNewTitle] = useState(hint.title ?? '');
  const [newCompany, setNewCompany] = useState(hint.company || email.companyHint || '');

  const suggestions = email.suggestions ?? [];
  const queueMatches = email.queueMatches ?? [];

  const expand = async () => {
    setOpen((v) => !v);
    if (body !== null) return;
    try {
      const r = await api.get(`/emails/${email._id}`);
      setBody(r.email?.bodyText ?? '');
    } catch {
      setBody('');
    }
  };

  const link = async (recommendationId) => {
    setBusy(true);
    try {
      await api.patch(`/emails/${email._id}`, { recommendationId });
      onLinked(email._id);
      toast('Linked.', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || 'Could not link that message.', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const createApplication = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setBusy(true);
    try {
      await api.post(`/emails/${email._id}/application`, { title, company: newCompany.trim() });
      onLinked(email._id);
      toast('Application recorded, and this message attached to it.', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || 'Could not record that application.', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-md border border-slate-800 bg-slate-900/40">
      <button
        type="button"
        onClick={expand}
        aria-expanded={open}
        className="flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-slate-800/60"
      >
        <span aria-hidden="true" className="mt-0.5 text-slate-500">{open ? '▾' : '▸'}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-slate-200">{email.subject || '(no subject)'}</span>
          <span className="mt-0.5 block truncate text-xs text-slate-500">
            {email.fromName ? `${email.fromName} · ` : ''}
            {email.from}
          </span>
        </span>
        <span className="shrink-0 text-xs tabular-nums text-slate-500">{fmt(email.sentAt)}</span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-slate-800 px-3 py-3">
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words font-sans text-xs leading-relaxed text-slate-300">
            {body === null ? 'loading…' : body || '(empty)'}
          </pre>

          {/* T372 — Claude picked this and the corroboration guard refused it,
              because the company it chose is nowhere in the message. Saying so
              beats both silently taking it and silently dropping it: he can see
              the guess, see why it was held back, and take it if he knows
              something the message does not say. */}
          {email.aiRejected && (
            <div className="rounded-md border border-amber-800 bg-amber-950/20 px-2 py-1.5">
              <p className="text-[11px] text-amber-300">
                Claude suggested <strong>{email.aiRejected.company}</strong>
                {email.aiRejected.title ? ` · ${email.aiRejected.title}` : ''}, but this message never
                mentions that company — so it was not linked.
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => link(email.aiRejected.id)}
                className="mt-1 text-[11px] text-amber-200 underline decoration-dotted transition hover:text-amber-100 disabled:opacity-50"
              >
                Link it anyway
              </button>
            </div>
          )}

          {/* T393 — when nothing plausible exists, LEAD with what the message
              says rather than with a picker that cannot help.
              
              The CBS confirmation had zero suggestions, because the job was
              never imported — so the card offered "pick one of your 87
              applications" and buried the only useful action, "add it", as a
              small link underneath. He picked one, and a Copenhagen Business
              School confirmation ended up filed on a SameSystem application
              from six weeks earlier.
              
              The reading was right all along: company CBS, role
              Infrastrukturarkitekt, straight out of the subject. It was never
              shown to him. So now it is the first thing on the card. */}
          {noMatchButReadable && (
            <div className="rounded-md border border-brand-700 bg-brand-600/5 p-2">
              <p className="text-[10px] uppercase tracking-wide text-brand-400">Read from this message</p>
              <p className="mt-1 text-sm text-slate-200">
                {hint.company || '—'}
                {hint.title ? <span className="text-slate-400"> · {hint.title}</span> : null}
              </p>
              <p className="mt-0.5 text-[11px] text-slate-500">
                Nothing in the tracker matches this, so it looks like an application hunter has never
                seen. Add it and the reply attaches to it.
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => setAdding(true)}
                className="mt-2 rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
              >
                Add this application
              </button>
            </div>
          )}

          {/* T445 — rows he HAS but has not marked applied yet.
              He imported the Spirii Cloud Engineer, applied, and this card
              offered him 103 applied rows (no Spirii — his was still `new`)
              and a button to create the application from scratch, which
              would have made a third Spirii row beside the one he had just
              imported. Above the applied list, because when the message
              names a company he is already tracking, that row is almost
              always the answer. */}
          {queueMatches.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">
                In your queue — did you apply to this?
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {queueMatches.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    disabled={busy}
                    onClick={() => link(c.id)}
                    title="Links the message here. The stage change arrives as a suggestion you approve."
                    className="rounded border border-brand-700 bg-brand-600/10 px-2 py-1 text-xs text-slate-200 transition hover:border-brand-500 hover:text-brand-300 disabled:opacity-50"
                  >
                    {c.company}
                    {c.title ? <span className="text-slate-400"> · {c.title}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-400">
              {suggestions.length > 0
                ? 'Link to'
                : noMatchButReadable
                  ? 'Or link it to one you already have'
                  : 'No obvious match — pick one'}
            </p>

            {suggestions.length > 0 && !showAll && (
              <>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {suggestions.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      disabled={busy}
                      onClick={() => link(c.id)}
                      title={WHY[c.why] ?? c.why}
                      className="rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-xs text-slate-300 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
                    >
                      {c.company}
                      {c.title ? <span className="text-slate-500"> · {c.title}</span> : null}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="mt-1.5 text-[11px] text-slate-500 underline decoration-dotted transition hover:text-slate-300"
                >
                  none of these — show all applications
                </button>
              </>
            )}

            {(suggestions.length === 0 || showAll) && (
              // T366 — a native <select> over 87 applications has no way in
              // except scrolling. This one is typed into.
              <ApplicationPicker
                candidatures={candidatures}
                disabled={busy}
                onPick={link}
                suggestedIds={new Set(suggestions.map((s) => s.id))}
              />
            )}
          </div>

          {/* T369 — the honest option when nothing fits. Three of the last four
              unlinked messages are from companies he applied to and the tracker
              never knew about; there is nothing to link them to, so the message
              becomes the record. */}
          {!adding ? (
            <div className="flex flex-wrap items-center gap-3">
              {!noMatchButReadable && (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="text-[11px] text-slate-400 underline decoration-dotted transition hover:text-brand-400"
                >
                  I applied here, but it is not in the tracker — add it
                </button>
              )}
              <button
                type="button"
                disabled={deleting}
                onClick={remove}
                className="text-[11px] text-slate-500 underline decoration-dotted transition hover:text-red-400 disabled:opacity-50"
              >
                Not relevant — delete this copy
              </button>
            </div>
          ) : (
            <div className="space-y-1.5 rounded-md border border-slate-800 bg-slate-950/40 p-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Record this application</p>
              <input
                value={newCompany}
                onChange={(e) => setNewCompany(e.target.value)}
                placeholder="Company"
                aria-label="Company"
                className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100 focus:border-brand-500 focus:outline-none"
              />
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Role you applied for"
                aria-label="Role"
                className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100 focus:border-brand-500 focus:outline-none"
              />
              <p className="text-[10px] text-slate-500">
                Dated {fmt(email.sentAt)}, from the message — better evidence of when you applied than
                today&rsquo;s date.
                {hint.title || hint.company ? ' Company and role are read from the message — check them.' : ''}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={busy || !newTitle.trim() || !newCompany.trim()}
                  onClick={createApplication}
                  className="rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
                >
                  Add application
                </button>
                <button
                  type="button"
                  onClick={() => setAdding(false)}
                  className="text-[11px] text-slate-500 underline decoration-dotted hover:text-slate-300"
                >
                  cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export default Row;
